import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Card,
  Col, Container, Row, Tab,
} from 'react-bootstrap';
import { faFileZipper } from '@fortawesome/free-solid-svg-icons';
import meta from '../../../../meta';
import { EditorControls, editorHooks, EditorRadioInput } from '../../../basicEditor/editorControls';
import { Footer, DocumentBody } from '../../../../components/Containers/docs';

import useEditor from '../../../newEditor/hook/editor';
import { DocCommandPanel } from '../../../newEditor/commandPanels';
import EditorContainer from '../../../newEditor/editorContainer';
import useEditorParams from '../../../newEditor/hook/params';
import HidableContainer from '../../common/hidableContainer';
import { CPButton } from '../../../../components/bootStrap/buttons';
import { usePrintFormEditor } from '../../../printForms/hook/usePrintFormEditor';
import { LabelTab, TabContainer } from '../../../basicEditor/tpController/styles';
import SignSend from './signSend';
import Equpmanet from './tp/equipment';
import SmokeChanel from './tp/tpSmoke';
import WindChanel from './tp/tpWind';
import { ReceivedStamp } from '../../../../components/stamp';
import StampSend from '../../../../components/stamp/stampSend';
import { CiatAppContext } from '../../../../providers';

const { backendURL } = meta.doc.aktDvk;

function Editor({
  onSave, onClose, id,
}) {
  const editorParams = useEditorParams();
  const {
    data, actions, fields, fieldErrors, nonFieldErrors, options, isNew, changed, permissions,
    loading, systemErrors, headerReadOnlyFields, printForms, headerProps,
  } = useEditor({
    backendURL,
    id,
    onCloseCallBack: onClose,
    onSaveCallBack: onSave,
    ...editorParams,
  });

  const { currentUser } = useContext(CiatAppContext);
  const isAdmin = currentUser.is_superuser;
  const dispatcher = currentUser.is_staff;

  const address = editorHooks.useStringInputProps('address', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const note = editorHooks.useStringInputProps('note', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const information = editorHooks.useStringInputProps('information', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const changeAirValueProps = editorHooks.useStringInputProps('change_air_value', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const placeProps = editorHooks.useStringInputProps('place', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const countChannelsProps = editorHooks.useNumberInputProps('count_channels', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const number = editorHooks.useDateInputProps('number', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const docDate = editorHooks.useDateInputProps('doc_date', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const auditor = editorHooks.useItemInputProps('auditor', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const ReadOnlyAuditor = editorHooks.useItemInputProps('read_only_auditor', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const contragent = editorHooks.useItemInputProps('contragent', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const ownerContragent = editorHooks.useItemInputProps('owner_contragent', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const personContragent = editorHooks.useItemInputProps('person_contragent', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const zvtChangeAirProps = editorHooks.useItemInputProps('zvt_change_air_ref', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const zvtWindVentProps = editorHooks.useItemInputProps('zvt_wind_vent_ref', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const isCleaningTrapProps = editorHooks.useCheckboxInputProps('is_cleaning_trap', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const isOkHeadProps = editorHooks.useCheckboxInputProps('is_ok_head', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const isOkWallProps = editorHooks.useSelectorInputProps('is_ok_wall', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const isOkWind = editorHooks.useCheckboxInputProps('is_ok_wind', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const channelSeparation = editorHooks.useSelectorInputProps('channel_separation', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const headReady = editorHooks.useSelectorInputProps('head_ready', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const windZone = editorHooks.useSelectorInputProps('wind_zone', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const brokenSmokeChannel = editorHooks.useSelectorInputProps('broken_smoke_channel', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const brokenVentChannel = editorHooks.useSelectorInputProps('broken_vent_channel', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const typeResearch = editorHooks.useSelectorInputProps('type_research', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const reasonUnusable = editorHooks.useTextInputProps('reason_unusable', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const province = editorHooks.useItemInputProps('province', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const localityProps = editorHooks.useItemInputProps('locality', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const districtProps = editorHooks.useItemInputProps('district', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const streetProps = editorHooks.useItemInputProps('street', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);
  const houseProps = editorHooks.useItemInputProps('house', data, fields, fieldErrors, actions.onChange, false, headerReadOnlyFields);

  const printActions = usePrintFormEditor({
    backendURL: '/api/akt_dvk/printform/akt_dvk/form_akt_dvk/',
    instanceId: data.id,
    repr: 'Акт двк',
  });

  const [vSuitable, setSuitable] = useState(false);

  const smokeEnable = useMemo(
    () => (data.aktdvksmokechanelrow_set ? ((data.aktdvksmokechanelrow_set.length > 0)) : false),
    [data.aktdvksmokechanelrow_set],
  );

  const ventEnable = useMemo(
    () => (data.aktdvkwindchanelrow_set ? ((data.aktdvkwindchanelrow_set.length > 0)) : false),
    [data.aktdvkwindchanelrow_set],
  );

  useEffect(() => {
    if (!smokeEnable) {
      actions.onChange({
        broken_smoke_channel: 0,
        is_ok_wall: 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smokeEnable]);

  useEffect(() => {
    if (!ventEnable) {
      actions.onChange({
        broken_vent_channel: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ventEnable]);

  const arrSuitable = [
    { value: true, display_name: 'придатні до експлуатації' },
    { value: false, display_name: 'непридатні до експлуатації ' },
  ];

  const setAddr = useCallback((newData) => {
    const locality = newData.locality ? newData.locality.name : '';
    const localityArea = newData.locality_area ? `, ${newData.locality_area.name}` : '';
    const street = newData.street ? `, ${newData.street.name}` : '';
    const house = newData.house ? `, ${newData.house.number}` : '';

    actions.onChange({
      ...newData,
      address: `${locality}${localityArea}${street}${house}`,
    });
  }, [actions]);

  useEffect(() => {
    actions.setReadOnly(data.is_signed);
  }, [actions, data.is_signed]);

  return (
    <EditorContainer
      isNew={isNew}
      name={options.name}
      repr={data.repr}
      isLoading={loading}
      err={systemErrors}
      nonFieldErrors={nonFieldErrors}
      onClearNonFieldErrors={actions.onClearNonFieldErrors}
      onClearErrors={actions.onClearErrs}
      onClose={actions.onClose}
      changed={changed}
      fields={fields}
      CommandPanel={(!systemErrors && (
        <DocCommandPanel
          backendURL={backendURL}
          permissions={permissions}
          actions={actions}
          changed={changed}
          executed={data.executed}
          printForms={!changed && printForms}
          id={id}
          repr={data.repr}
        >
          <CPButton
            onClick={() => printActions?.actions?.onDowloadP7s(id)}
            content="Завантажити підписаний акт"
            disabled={!data.executed}
            icon={faFileZipper}
          />
        </DocCommandPanel>
      ))}
    >
      {!systemErrors && (
      <>
        <DocumentBody>

          <HidableContainer>
            <Container fluid style={{ padding: '0' }}>
              <Row>
                <Col lg={1}>
                  <EditorControls.StringInput {...number} />
                </Col>
                <Col lg={1}>
                  <EditorControls.DateInput {...docDate} />
                </Col>
                <Col lg={2}>
                  <EditorControls.SelectorInput {...typeResearch} />
                </Col>
                <Col lg={2}>
                  <EditorControls.NumberInput {...countChannelsProps} />
                </Col>
                <Col>
                  <EditorControls.ItemPicker
                    {...auditor}
                    // filter={{ is_group: false }}
                    disabled={ReadOnlyAuditor.value}
                    // noHierarchy
                  />
                </Col>
                <Col lg={2}>
                  <EditorControls.ItemPicker {...contragent} />
                </Col>
                <Col lg={2}>
                  <EditorControls.ItemPicker
                    {...ownerContragent}
                    filter={{
                      contragent: data?.contragent?.id,
                    }}
                    disabled={!data?.contragent}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg={2}>
                  <EditorControls.ItemPicker
                    {...province}
                    onChange={(e, v) => {
                      actions.onChange({
                        province: v,
                        street: null,
                        locality: null,
                        district: null,
                        house: null,
                      });
                    }}
                  />
                </Col>
                <Col lg={2}>
                  <EditorControls.ItemPicker
                    {...districtProps}
                    label="Район"
                    onChange={(e, v) => {
                      actions.onChange({
                        district: v,
                        street: null,
                        locality: null,
                        house: null,
                      });
                      setAddr({
                        ...data,
                        district: v,
                        street: null,
                        locality: null,
                        house: null,
                      });
                    }}
                    filter={{ province: data.province?.id }}
                    disabled={!data.province}
                  />
                </Col>

                <Col lg={3}>
                  <EditorControls.ItemPicker
                    {...localityProps}
                    onChange={(e, v) => {
                      actions.onChange({
                        locality: v,
                        place: v.repr,
                      });
                      setAddr({
                        ...data,
                        locality: v,
                        street: null,
                        house: null,
                      });
                    }}
                    filter={{ district: data.district?.id }}
                    disabled={!data.district}
                  />
                </Col>

                <Col>
                  <EditorControls.ItemPicker
                    {...streetProps}
                    onChange={(e, v) => {
                      actions.onChange({
                        street: v,
                        house: null,
                      });
                      setAddr({
                        ...data,
                        street: v,
                        house: null,
                      });
                    }}
                    filter={{ locality: data.locality?.id }}
                    disabled={!data.locality}
                  />
                </Col>
                <Col lg={2}>
                  <EditorControls.ItemPicker
                    {...houseProps}
                    onChange={(e, v) => {
                      actions.onChange({
                        house: v,
                      });
                      setAddr({
                        ...data,
                        house: v,
                      });
                    }}
                    disabled={!data.street}
                    filter={{ street: data.street?.id }}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <EditorControls.TextInput {...address} />
                </Col>
                <Col lg={4}>
                  <EditorControls.StringInput
                    {...placeProps}
                    onC
                  />
                </Col>
              </Row>
            </Container>
          </HidableContainer>

          {data.approved && (
            <div className="stamp-container">
              <ReceivedStamp invoice={data.invoice} />
            </div>
          )}

          {data.is_sent && (
            <div className="stamp-container me-11">
              <StampSend invoice={data.sent_information} />
            </div>
          )}

          <LabelTab defaultActiveKey="main">
            <Tab eventKey="main" title="Загальні відомості">
              <TabContainer className="p-0">
                <Row>

                  <Col className="m-2">
                    {fields && fields.aktdvkequpmanetrow_set && (
                      <Equpmanet
                        data={data}
                        fields={fields}
                        errors={fieldErrors}
                        onChange={actions.onChange}
                        readOnly={!permissions.canChange}
                        tableName="aktdvkequpmanetrow_set"
                        onDraft={actions.onDraft}
                      />
                    )}
                  </Col>

                </Row>

                <Row>
                  <Col>

                    <Card className="border-0 p-3 ms-3">
                      <Card.Title>
                        Засоби вимірювальної техніки
                      </Card.Title>
                      <Card.Body className="p-1">
                        <Row>
                          <Col>
                            <EditorControls.ItemPicker
                              {...zvtChangeAirProps}
                              label="для повітрообміну "
                            />
                            {(data.zvt_wind_vent_ref) && (
                              <Badge bg="info" className="me-2">
                                {data.zvt_wind_vent_name}
                                {data.zvt_wind_vent_num}
                                {data.zvt_wind_vent_cheking_date}
                              </Badge>
                            )}

                          </Col>
                          <Col>
                            <EditorControls.ItemPicker {...zvtWindVentProps} />
                            {(data.zvt_change_air_ref) && (
                              <Badge bg="info" className="ms-2">
                                {data.zvt_change_air_name}
                                {data.zvt_change_air_num}
                                {data.zvt_change_air_cheking_date}
                              </Badge>
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                  </Col>
                  <Col>
                    <Card className="border-0 p-3">
                      <Card.Title>
                        Додаткові відомості
                      </Card.Title>
                      <Card.Body className="p-1">
                        <EditorControls.TextInput {...information} rows={2} />
                      </Card.Body>
                    </Card>

                  </Col>

                </Row>
              </TabContainer>

            </Tab>
            <Tab eventKey="result" title="Результати перевірки">
              <TabContainer className="p-0">

                <Card className="border-0 p-3 m-3">
                  <Card.Title>
                    Результати вимірів

                  </Card.Title>

                  <Card.Body className="p-1">
                    <Row>
                      <Col>
                        <Card className="border-0 p-3 my-2">
                          <Card.Title>
                            Вентиляційні канали
                          </Card.Title>
                          <Card.Body className="p-1">
                            {fields && fields.aktdvkwindchanelrow_set && (
                              <WindChanel
                                data={data}
                                fields={fields}
                                errors={fieldErrors}
                                onChange={actions.onChange}
                                readOnly={!permissions.canChange}
                                tableName="aktdvkwindchanelrow_set"
                                onDraft={actions.onDraft}
                              />
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="border-0 p-3 my-2">
                          <Card.Title>
                            Димові канали
                          </Card.Title>
                          <Card.Body className="p-1">
                            {fields && fields.aktdvksmokechanelrow_set && (
                              <SmokeChanel
                                data={data}
                                fields={fields}
                                errors={fieldErrors}
                                onChange={actions.onChange}
                                readOnly={!permissions.canChange}
                                tableName="aktdvksmokechanelrow_set"
                                onDraft={actions.onDraft}
                              />
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
                <Card className="border-0 p-3 m-3">
                  <Card.Title>
                    Результати перевірки
                  </Card.Title>

                  <Card.Body className="p-1">
                    <Row>
                      <Col>
                        <Card className="border-0 p-3">
                          <EditorControls.StringInput {...changeAirValueProps} />
                          <EditorControls.SelectorInput
                            {...brokenSmokeChannel}
                            disabled={!smokeEnable}
                          />

                          <EditorControls.SelectorInput
                            {...brokenVentChannel}
                            disabled={!ventEnable}
                          />
                        </Card>
                      </Col>

                      <Col>
                        <Card className="border-0 p-3">
                          <EditorControls.SelectorInput {...channelSeparation} />

                          <EditorControls.SelectorInput {...headReady} />

                          <EditorControls.SelectorInput {...windZone} />
                        </Card>
                      </Col>

                      <Col>
                        <Card className="border-0 p-3">

                          <EditorControls.SelectorInput
                            {...isOkWallProps}
                            disabled={!smokeEnable}
                          />

                          <EditorControls.CheckboxInput {...isOkWind} />

                          <EditorControls.CheckboxInput {...isOkHeadProps} />

                          <EditorControls.CheckboxInput {...isCleaningTrapProps} />
                        </Card>
                      </Col>

                    </Row>
                  </Card.Body>
                </Card>
                <Card className="border-0 p-3 m-3">
                  <Card.Title>
                    Висновок
                  </Card.Title>
                  <Card.Body className="p-1">
                    <Row>
                      <Col>
                        <Card className="border-0 p-3 m-3">
                          <Card.Title />
                          <Card.Body className="p-1">
                            <EditorRadioInput
                              label="Димові і вентиляційні канали "
                              inline
                              value={vSuitable}
                              onChange={(e, value) => {
                                setSuitable(value);
                                actions.onChange({
                                  suitable: value,
                                });
                              }}
                              values={arrSuitable}
                            />
                          </Card.Body>
                        </Card>

                      </Col>
                      <Col>
                        <EditorControls.TextInput
                          {...reasonUnusable}
                          disabled={vSuitable}
                          rows={4}
                        />
                        <EditorControls.ItemPicker
                          {...personContragent}
                          filter={{
                            contragent: data?.contragent?.id,
                          }}
                          disabled={data?.contragent && vSuitable}
                          label="Попереджено про заборону  користування"
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

              </TabContainer>

            </Tab>
            <Tab eventKey="sign" title="Підписання">
              <TabContainer className="p-0">
                <Row>
                  <Col>
                    {(!systemErrors && (
                      <SignSend
                        id={id}
                        currentSign={data.person_kep}
                        repr={data.repr}
                        isSigned={(data.is_signed && (isAdmin || dispatcher))}
                        isDispatcher={(isAdmin || dispatcher)}
                        emailGrm={data.email_for_akt}
                        emailClient={data.contragent_email}
                        onChange={actions.onChange}
                        onSaveWithoutExit={actions.onSaveWithoutExit}
                      />
                    ))}
                  </Col>
                </Row>
              </TabContainer>
            </Tab>
            <Tab title="Примітка" eventKey="notes">
              <Container fluid className="mx-1 my-2">
                <EditorControls.TextInput
                  {...note}
                  rows={5}
                />
              </Container>
            </Tab>
          </LabelTab>

        </DocumentBody>

        <Footer
          created={data.created_at}
          updated={data.updated_at}
          readOnly={!permissions.canChange}
          inspection={data.inspection}
          authority={data.authority}
          author={data.author}
          onChange={actions.onChange}
          id={id}
          headerProps={headerProps}
        />
      </>
      )}
    </EditorContainer>
  );
}

Editor.propTypes = {
  id: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
};

Editor.defaultProps = {
  onClose: null,
  onSave: null,
};

export default Editor;
